import { Alert, Linking, Platform } from 'react-native';

/** Only http(s) may be opened here — some fields this feeds (e.g. a shop
 * owner's free-text googleReviewLink) are not scheme-validated server-side,
 * so a malicious/compromised owner account could otherwise set something
 * like tel:/sms:/intent:// and get a reviewer to tap it from what looks like
 * trusted in-app UI. */
function isHttpUrl(url: string): boolean {
  try {
    const scheme = new URL(url).protocol;
    return scheme === 'http:' || scheme === 'https:';
  } catch {
    return false;
  }
}

/** Linking.openURL can reject (e.g. no app registered to handle it, or a
 * transient OS-level failure) — swallow that into a friendly alert instead of
 * an unhandled promise rejection crashing the screen with a red error box. */
export async function openExternalUrl(url: string) {
  if (!isHttpUrl(url)) {
    Alert.alert('Liên kết không hợp lệ', 'Đường dẫn này không thể mở được.');
    return;
  }
  try {
    await Linking.openURL(url);
  } catch {
    Alert.alert(
      'Không mở được liên kết',
      `Thiết bị không mở được đường dẫn này. Bạn có thể sao chép và dán vào trình duyệt:\n\n${url}`,
    );
  }
}

/** Turn-by-turn directions to a coordinate. Tries the native Google Maps app
 * first (most reviewers in VN have it, and it matches the shop's pasted
 * Google Maps link) via its comgooglemaps:// scheme; Linking.openURL can
 * open another app's scheme directly without any whitelisting, so this
 * needs no extra native config. Falls back to the Google Maps web link
 * (opens the app if installed, else the website) if the scheme fails. */
export async function openDirections(lat: number, lng: number) {
  // On web (PWA build) a custom app scheme doesn't reject even when nothing
  // handles it, so the try/catch below can't detect failure there — go
  // straight to the web link, which the browser can always open.
  if (Platform.OS !== 'web') {
    try {
      await Linking.openURL(`comgooglemaps://?daddr=${lat},${lng}&directionsmode=driving`);
      return;
    } catch {
      // Google Maps app not installed — fall through to the web link below.
    }
  }
  return openExternalUrl(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`);
}

/** Web only: there is no API for a website to silently write into the phone's
 * photo library — browsers deliberately don't allow that. The closest
 * equivalent is the Web Share API's file-sharing mode, which brings up the
 * OS share sheet with a native "Save Image"/"Save to Photos" option (iOS
 * Safari, Chrome Android both support this). Falls back to just opening the
 * image in a new tab (the old behavior — tap and hold to save manually) on
 * browsers where the Share API or file-sharing isn't available. */
export async function saveImageOnWeb(url: string, filename: string) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
    const nav = navigator as Navigator & { canShare?: (data: { files: File[] }) => boolean };
    if (nav.share && nav.canShare?.({ files: [file] })) {
      await nav.share({ files: [file] });
      return;
    }
  } catch {
    // Fall through — either fetch/share failed, or the user cancelled the
    // share sheet (which also rejects); either way just show the image.
  }
  window.open(url, '_blank');
}
